// components/StartTrialModal.jsx
import React, { useState, useEffect } from 'react';
import { X, Gift,  AlertCircle, Users, Database } from 'lucide-react';
import { useToast } from './ToastContainer';

const StartTrialModal = ({ isOpen, onClose, tenant, plans, onSubmit, isSubmitting }) => {
  const { error: showError } = useToast();
  
  const [formData, setFormData] = useState({
    planId: '',
    trialDays: 14,
    featuresDuringTrial: 'limited'
  });

  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    if (tenant && isOpen) {
      setFormData({
        planId: tenant.planId || '',
        trialDays: 14,
        featuresDuringTrial: 'limited'
      });
      
      if (tenant.planId) {
        const plan = plans.find(p => p.planId === tenant.planId);
        setSelectedPlan(plan);
        if (plan?.trial?.durationDays) {
          setFormData(prev => ({
            ...prev,
            trialDays: plan.trial.durationDays
          }));
        }
      }
    }
  }, [tenant, isOpen, plans]);

  if (!isOpen || !tenant) return null;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    if (name === 'planId') {
      const plan = plans.find(p => p.planId === value);
      setSelectedPlan(plan);
      if (plan?.trial?.durationDays) {
        setFormData(prev => ({
          ...prev,
          trialDays: plan.trial.durationDays
        }));
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.planId) {
      showError('Please select a plan');
      return;
    }
    
    if (!formData.trialDays || formData.trialDays < 1) {
      showError('Please enter a valid number of trial days');
      return;
    }

    onSubmit(tenant.tenantId, {
      planId: formData.planId,
      trialDays: parseInt(formData.trialDays),
      featuresDuringTrial: formData.featuresDuringTrial
    });
  };

  const planHasTrial = selectedPlan?.trial?.enabled;
  const maxTrialDays = selectedPlan?.trial?.durationDays || 14;
  const limitedFeatures = selectedPlan?.trial?.limitedFeatures || {
    maxStudents: 5,
    maxStaff: 2,
    maxBranches: 1,
    maxStorageGB: 1
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Start Free Trial</h2>
            <p className="text-sm text-slate-500">
              {tenant.tenantName || tenant.tenantId}
            </p>
          </div>
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Plan Selection */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Select Plan <span className="text-red-500">*</span>
            </label>
            <select
              name="planId"
              value={formData.planId}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500 bg-white"
              required
            >
              <option value="">Select a plan</option>
              {plans.filter(p => p.isActive && p.trial?.enabled).map((plan) => (
                <option key={plan.planId} value={plan.planId}>
                  {plan.name} - {plan.trial.durationDays} days trial
                </option>
              ))}
            </select>
            {selectedPlan && !planHasTrial && (
              <p className="mt-1 text-xs text-amber-600">
                This plan does not have trial enabled
              </p>
            )}
          </div>

          {/* Trial Duration */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Trial Duration (Days) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              name="trialDays"
              value={formData.trialDays}
              onChange={handleChange}
              min="1"
              max={maxTrialDays}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-500"
              placeholder="14"
              required
            />
            {selectedPlan && (
              <p className="mt-1 text-xs text-slate-500">
                Max allowed: {maxTrialDays} days
              </p>
            )}
          </div>

          {/* Features Mode */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Features During Trial
            </label>
            <div className="space-y-2">
              <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
                <input
                  type="radio"
                  name="featuresDuringTrial"
                  value="limited"
                  checked={formData.featuresDuringTrial === 'limited'}
                  onChange={handleChange}
                  className="w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-500"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900">Limited Features</p>
                  <p className="text-xs text-slate-500">Access to basic features only</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-3 border border-slate-200 rounded-xl cursor-pointer hover:bg-slate-50 transition-colors">
                <input
                  type="radio"
                  name="featuresDuringTrial"
                  value="full"
                  checked={formData.featuresDuringTrial === 'full'}
                  onChange={handleChange}
                  className="w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-500"
                />
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900">Full Features</p>
                  <p className="text-xs text-slate-500">Access to all plan features</p>
                </div>
              </label>
            </div>
          </div>

          {/* Limited Features Preview */}
          {formData.featuresDuringTrial === 'limited' && selectedPlan && (
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
              <p className="text-xs font-medium text-slate-700 mb-2">Limited Features Include:</p>
              <div className="grid grid-cols-2 gap-1 text-xs text-slate-600">
                <div className="flex items-center gap-1">
                  <Users className="w-3 h-3 text-slate-400" />
                  Max Students: {limitedFeatures.maxStudents || 5}
                </div>
                <div className="flex items-center gap-1">
                  <Users className="w-3 h-3 text-slate-400" />
                  Max Staff: {limitedFeatures.maxStaff || 2}
                </div>
                <div className="flex items-center gap-1">
                  <Database className="w-3 h-3 text-slate-400" />
                  Max Branches: {limitedFeatures.maxBranches || 1}
                </div>
                <div className="flex items-center gap-1">
                  <Database className="w-3 h-3 text-slate-400" />
                  Max Storage: {limitedFeatures.maxStorageGB || 1} GB
                </div>
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-700">
                <p className="font-semibold">What happens after trial?</p>
                <ul className="list-disc list-inside mt-1 space-y-0.5">
                  <li>Trial will automatically expire after {formData.trialDays} days</li>
                  <li>Tenant will lose access unless subscription is activated</li>
                  <li>You can extend or activate the subscription anytime</li>
                </ul>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex-shrink-0 bg-white border-t border-slate-200 px-6 py-4 flex items-center gap-3 rounded-b-2xl">
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={isSubmitting || !planHasTrial}
            className="flex-1 px-6 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Gift className="w-4 h-4" />
            {isSubmitting ? 'Starting...' : 'Start Trial'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-6 py-2.5 border border-slate-200 text-slate-600 text-sm font-medium rounded-xl hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default StartTrialModal;